import { DataType } from '../DataType';
import { Ident as UniqueId } from '../Ident';
import { ExprValue } from './ExprValue';
export var ExprNode;
(function (ExprNode) {
    let Type;
    (function (Type) {
        Type["Ident"] = "IDENT";
        Type["Param"] = "PARAM";
        Type["Access"] = "ACCESS";
        Type["CallName"] = "CALL_NAME";
        Type["ListLiteral"] = "LIST_LITERAL";
        Type["NullLiteral"] = "NULL_LITERAL";
        Type["BooleanLiteral"] = "BOOLEAN_LITERAL";
        Type["NumberLiteral"] = "NUMBER_LITERAL";
        Type["StringLiteral"] = "STRING_LITERAL";
        Type["RegexpLiteral"] = "REGEXP_LITERAL";
        Type["DateTimeLiteral"] = "DATE_TIME_LITERAL";
        Type["Call"] = "CALL";
        Type["Cast"] = "CAST";
        Type["Case"] = "CASE";
        Type["CaseWhen"] = "CASE_COND";
        Type["CaseElse"] = "CASE_ELSE";
        Type["IfElse"] = "IF_ELSE";
        Type["IfCond"] = "IF_COND";
        Type["Unary"] = "UNARY";
        Type["Binary"] = "BINARY";
        Type["Logical"] = "LOGICAL";
    })(Type = ExprNode.Type || (ExprNode.Type = {}));
    function makeLiteral(value, type, role) {
        if (value == null) {
            return ExprNode.NullLiteral.make(role);
        }
        switch (type.name) {
            case "STRING": {
                if (typeof value === 'string') {
                    return ExprNode.StringLiteral.make(value, role);
                }
                break;
            }
            case "DATE_TIME": {
                switch (typeof value) {
                    case 'number':
                    case 'string': {
                        const parsed = +value;
                        if (Number.isFinite(parsed)) {
                            return ExprNode.DateTimeLiteral.make(parsed, role);
                        }
                    }
                }
                break;
            }
            case "TIMESTAMP": {
                switch (typeof value) {
                    case 'number':
                    case 'string': {
                        const parsed = +value;
                        if (Number.isFinite(parsed)) {
                            return ExprNode.NumberLiteral.make(parsed, { type, role });
                        }
                    }
                }
                break;
            }
            case "BOOLEAN": {
                if (typeof value === 'boolean') {
                    return ExprNode.BooleanLiteral.make(value, role);
                }
                break;
            }
            case "FLOAT":
            case "DECIMAL":
            case "INTEGER": {
                if (typeof value === 'boolean')
                    break;
                let parsed = ExprValue.parseNumber(value);
                if (ExprValue.isSafeNumber(parsed)) {
                    parsed = +parsed;
                }
                if (type.name === "INTEGER") {
                    const absValue = ExprValue.asDecimal(parsed).abs();
                    if (absValue.lt(2 ** 31)) {
                        return ExprNode.NumberLiteral.make(parsed, {
                            type,
                            role,
                        });
                    }
                    else {
                        return ExprNode.NumberLiteral.make(parsed, {
                            type: DataType.FloatType.make(),
                            role,
                        });
                    }
                }
                return ExprNode.NumberLiteral.make(parsed, {
                    type,
                    role,
                });
            }
        }
        throw new Error(`Cannot make literal from "${typeof value}" with ` +
            `data type ${type.name}, role ${role}`);
    }
    ExprNode.makeLiteral = makeLiteral;
    let Ident;
    (function (Ident) {
        function make(name) {
            const id = UniqueId.next('R');
            const stage = "CONST";
            return { id, name, stage, type: "IDENT" };
        }
        Ident.make = make;
        function parsed(source, name) {
            return { source, ...make(name) };
        }
        Ident.parsed = parsed;
    })(Ident = ExprNode.Ident || (ExprNode.Ident = {}));
    let Param;
    (function (Param) {
        function make(name, data) {
            const id = UniqueId.next('R');
            const stage = "CONST";
            return { name, id, stage, data, type: "PARAM" };
        }
        Param.make = make;
    })(Param = ExprNode.Param || (ExprNode.Param = {}));
    let NullLiteral;
    (function (NullLiteral) {
        function make(role) {
            const id = UniqueId.next('R');
            const stage = "CONST";
            const data = { type: DataType.NullType.make(), role };
            return { id, stage, data, value: null, type: "NULL_LITERAL" };
        }
        NullLiteral.make = make;
        function parsed(source, role) {
            return { source, ...make(role) };
        }
        NullLiteral.parsed = parsed;
    })(NullLiteral = ExprNode.NullLiteral || (ExprNode.NullLiteral = {}));
    let BooleanLiteral;
    (function (BooleanLiteral) {
        function make(value, role) {
            const id = UniqueId.next('R');
            const stage = "CONST";
            const data = { type: DataType.BooleanType.make(), role };
            return { id, value, stage, data, type: "BOOLEAN_LITERAL" };
        }
        BooleanLiteral.make = make;
        function parsed(source, value, role) {
            return { source, ...make(value, role) };
        }
        BooleanLiteral.parsed = parsed;
    })(BooleanLiteral = ExprNode.BooleanLiteral || (ExprNode.BooleanLiteral = {}));
    let NumberLiteral;
    (function (NumberLiteral) {
        function make(value, data) {
            const id = UniqueId.next('R');
            const stage = "CONST";
            return { id, value, stage, data, type: "NUMBER_LITERAL" };
        }
        NumberLiteral.make = make;
        function parsed(source, value, data) {
            return { source, ...make(value, data) };
        }
        NumberLiteral.parsed = parsed;
    })(NumberLiteral = ExprNode.NumberLiteral || (ExprNode.NumberLiteral = {}));
    let StringLiteral;
    (function (StringLiteral) {
        function make(value, role) {
            const id = UniqueId.next('R');
            const stage = "CONST";
            const data = { type: DataType.StringType.make(), role };
            return { id, value, stage, data, type: "STRING_LITERAL" };
        }
        StringLiteral.make = make;
        function parsed(source, value, role) {
            return { source, ...make(value, role) };
        }
        StringLiteral.parsed = parsed;
    })(StringLiteral = ExprNode.StringLiteral || (ExprNode.StringLiteral = {}));
    let RegexpLiteral;
    (function (RegexpLiteral) {
        function make(value, role) {
            const id = UniqueId.next('R');
            const stage = "CONST";
            const data = { type: DataType.RegexpType.make(), role };
            return { id, value, stage, data, type: "REGEXP_LITERAL" };
        }
        RegexpLiteral.make = make;
        function parsed(source, value, role) {
            return { source, ...make(value, role) };
        }
        RegexpLiteral.parsed = parsed;
    })(RegexpLiteral = ExprNode.RegexpLiteral || (ExprNode.RegexpLiteral = {}));
    let DateTimeLiteral;
    (function (DateTimeLiteral) {
        function make(value, role) {
            const id = UniqueId.next('R');
            const stage = "CONST";
            const format = "ISO8601";
            const data = { type: DataType.DateTimeType.make(format), role };
            return { id, value, stage, data, type: "DATE_TIME_LITERAL" };
        }
        DateTimeLiteral.make = make;
        function parsed(source, value, role) {
            return { source, ...make(value, role) };
        }
        DateTimeLiteral.parsed = parsed;
    })(DateTimeLiteral = ExprNode.DateTimeLiteral || (ExprNode.DateTimeLiteral = {}));
    let ListLiteral;
    (function (ListLiteral) {
        function make(values, data) {
            const id = UniqueId.next('R');
            const stage = "CONST";
            return { id, values, stage, data, type: "LIST_LITERAL" };
        }
        ListLiteral.make = make;
    })(ListLiteral = ExprNode.ListLiteral || (ExprNode.ListLiteral = {}));
    let Access;
    (function (Access) {
        function make(path, data) {
            const id = UniqueId.next('R');
            const stage = "SCALAR";
            return { path, id, stage, data, type: "ACCESS" };
        }
        Access.make = make;
        function makeAggregate(path, data) {
            const id = UniqueId.next('R');
            const stage = "AGGREGATE";
            return { path, id, stage, data, type: "ACCESS" };
        }
        Access.makeAggregate = makeAggregate;
        function parsed(source, path, data) {
            return { source, ...make(path, data) };
        }
        Access.parsed = parsed;
    })(Access = ExprNode.Access || (ExprNode.Access = {}));
    let CallName;
    (function (CallName) {
        function make(name) {
            const id = UniqueId.next('R');
            const stage = "CONST";
            name = name.toLocaleUpperCase();
            return { id, name, stage, type: "CALL_NAME" };
        }
        CallName.make = make;
    })(CallName = ExprNode.CallName || (ExprNode.CallName = {}));
    let Call;
    (function (Call) {
        function make(call, callee, args, stage, data) {
            const id = UniqueId.next('R');
            return { call, callee, args, id, stage, data, type: "CALL" };
        }
        Call.make = make;
        function parsed(source, call, callee, args, stage, data) {
            return { source, ...make(call, callee, args, stage, data) };
        }
        Call.parsed = parsed;
    })(Call = ExprNode.Call || (ExprNode.Call = {}));
    let Cast;
    (function (Cast) {
        function make(operand, asType, stage, data) {
            const id = UniqueId.next('R');
            return { id, operand, asType, stage, data, type: "CAST" };
        }
        Cast.make = make;
    })(Cast = ExprNode.Cast || (ExprNode.Cast = {}));
    let Case;
    (function (Case) {
        function make(value, conds, stage, data) {
            const id = UniqueId.next('R');
            return { id, value, conds, stage, data, type: "CASE" };
        }
        Case.make = make;
        function makeWithElse(value, conds, elseNode, stage, data) {
            const id = UniqueId.next('R');
            return { id, value, conds, else: elseNode, stage, data, type: "CASE" };
        }
        Case.makeWithElse = makeWithElse;
    })(Case = ExprNode.Case || (ExprNode.Case = {}));
    let CaseWhen;
    (function (CaseWhen) {
        function make(when, then, stage, data) {
            const id = UniqueId.next('R');
            return { id, when, then, stage, data, type: "CASE_COND" };
        }
        CaseWhen.make = make;
    })(CaseWhen = ExprNode.CaseWhen || (ExprNode.CaseWhen = {}));
    let CaseElse;
    (function (CaseElse) {
        function make(then, stage, data) {
            const id = UniqueId.next('R');
            return { id, then, stage, data, type: "CASE_ELSE" };
        }
        CaseElse.make = make;
    })(CaseElse = ExprNode.CaseElse || (ExprNode.CaseElse = {}));
    let IfElse;
    (function (IfElse) {
        function make(base, conds, stage, data) {
            const id = UniqueId.next('R');
            return { id, base, conds, stage, data, type: "IF_ELSE" };
        }
        IfElse.make = make;
        function makeWithElse(base, conds, elseNode, stage, data) {
            const id = UniqueId.next('R');
            return {
                id,
                base,
                conds,
                else: elseNode,
                stage,
                data,
                type: "IF_ELSE",
            };
        }
        IfElse.makeWithElse = makeWithElse;
    })(IfElse = ExprNode.IfElse || (ExprNode.IfElse = {}));
    let IfCond;
    (function (IfCond) {
        function make(cond, result, stage, data) {
            const id = UniqueId.next('R');
            return { id, cond, result, stage, data, type: "IF_COND" };
        }
        IfCond.make = make;
    })(IfCond = ExprNode.IfCond || (ExprNode.IfCond = {}));
    let Unary;
    (function (Unary) {
        function make(operator, operand, stage, data) {
            const id = UniqueId.next('R');
            return { id, operator, operand, stage, data, type: "UNARY" };
        }
        Unary.make = make;
        function parsed(source, operator, operand, stage, data) {
            return { source, ...make(operator, operand, stage, data) };
        }
        Unary.parsed = parsed;
    })(Unary = ExprNode.Unary || (ExprNode.Unary = {}));
    let Binary;
    (function (Binary) {
        function make(operator, left, right, stage, data) {
            const id = UniqueId.next('R');
            return { id, operator, left, right, stage, data, type: "BINARY" };
        }
        Binary.make = make;
        function parsed(source, operator, left, right, stage, data) {
            return { source, ...make(operator, left, right, stage, data) };
        }
        Binary.parsed = parsed;
    })(Binary = ExprNode.Binary || (ExprNode.Binary = {}));
    let Logical;
    (function (Logical) {
        function make(operator, left, right, stage, data) {
            const id = UniqueId.next('R');
            return { operator, left, right, id, stage, data, type: "LOGICAL" };
        }
        Logical.make = make;
        function parsed(source, operator, left, right, stage, data) {
            return { source, ...make(operator, left, right, stage, data) };
        }
        Logical.parsed = parsed;
    })(Logical = ExprNode.Logical || (ExprNode.Logical = {}));
})(ExprNode || (ExprNode = {}));
(function (ExprNode) {
    function reduce(initial, node, reducer, parent) {
        forEachChildRecursively(node, function applyReducer(node, parent) {
            initial = reducer(initial, node, parent);
        });
        return reducer(initial, node, parent);
    }
    ExprNode.reduce = reduce;
    function forEachChildRecursively(root, nodeCb) {
        if (root == null) {
            return;
        }
        const queue = [];
        const nodes = [];
        forEachChild(root, (childNode) => {
            queue.push([childNode, root]);
        });
        while (queue.length !== 0) {
            const [node, parent] = queue.pop();
            nodes.push([node, parent]);
            forEachChild(node, (childNode) => {
                queue.push([childNode, node]);
            });
        }
        let length = nodes.length;
        while (length--) {
            const [node, parent] = nodes[length];
            nodeCb(node, parent);
        }
        nodes.length = 0;
    }
    ExprNode.forEachChildRecursively = forEachChildRecursively;
    function forEachChild(node, nodeCb) {
        if (node == null) {
            return;
        }
        switch (node.type) {
            case "NULL_LITERAL":
            case "BOOLEAN_LITERAL":
            case "NUMBER_LITERAL":
            case "STRING_LITERAL":
            case "DATE_TIME_LITERAL":
            case "REGEXP_LITERAL":
            case "PARAM":
            case "IDENT":
            case "CALL_NAME": {
                break;
            }
            case "LIST_LITERAL": {
                node.values.forEach((literal) => {
                    nodeCb(literal);
                });
                break;
            }
            case "ACCESS": {
                node.path.forEach((ident) => {
                    nodeCb(ident);
                });
                break;
            }
            case "CALL": {
                nodeCb(node.callee);
                node.args.forEach((arg) => {
                    nodeCb(arg);
                });
                break;
            }
            case "CAST": {
                nodeCb(node.operand);
                break;
            }
            case "UNARY": {
                nodeCb(node.operand);
                break;
            }
            case "BINARY": {
                nodeCb(node.left);
                nodeCb(node.right);
                break;
            }
            case "LOGICAL": {
                nodeCb(node.left);
                nodeCb(node.right);
                break;
            }
            case "CASE": {
                nodeCb(node.value);
                node.conds.forEach((cond) => {
                    nodeCb(cond);
                });
                if (node.else != null) {
                    nodeCb(node.else);
                }
                break;
            }
            case "CASE_COND": {
                nodeCb(node.when);
                nodeCb(node.then);
                break;
            }
            case "CASE_ELSE": {
                nodeCb(node.then);
                break;
            }
            case "IF_ELSE": {
                nodeCb(node.base);
                node.conds.forEach((cond) => {
                    nodeCb(cond);
                });
                if (node.else != null) {
                    nodeCb(node.else);
                }
                break;
            }
            case "IF_COND": {
                nodeCb(node.cond);
                nodeCb(node.result);
                break;
            }
        }
    }
    ExprNode.forEachChild = forEachChild;
})(ExprNode || (ExprNode = {}));
